import { ReactElement, useEffect, useState } from 'react';
import { SearchOutlined } from '@ant-design/icons';
import { keepPreviousData } from '@tanstack/react-query';
import { MenuProps } from 'antd';
import Form, { useForm } from 'antd/es/form/Form';
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import { useLoaderData, useNavigate } from 'react-router-dom';
import { queryKeysClient } from 'common/clients/query-key.client';
import { queryClient } from 'common/clients/query.client';
import { Button } from 'common/components/antd/Button/Button';
import { FormItem } from 'common/components/antd/Form/FormItem';
import { Input } from 'common/components/antd/Input/Input';
import { Select } from 'common/components/antd/Select/Select';
import { Space } from 'common/components/antd/Space/Space';
import { Table } from 'common/components/antd/Table/Table';
import { ENV_COUNTERPARTY_GUID } from 'common/constants/env.constants';
import { IS_ADMIN_FE, IS_BANK_FE } from 'common/constants/general-access-role.constants';
import { PERSISTENCE_KEYS } from 'common/constants/persistence.constants';
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from 'common/constants/table.constants';
import { EQueryKeys } from 'common/enums/query-kyes.enum';
import { EEmissionAccountColumnKeys } from 'domains/accounts/constants/accounts.constants';
import {
  useCounterpartyAccountsListQuery,
  useCounterpartyBranchAccountsListQuery
} from 'domains/accounts/queries/accounts.query';
import { selectedCounterpartyAtom } from 'domains/accounts/store/selected-counterparty';
import { EAccountKind, TAccountsListResData } from 'domains/accounts/types/accounts.types';
import { makeAccountFilterByGUIDOrIBAN } from 'domains/accounts/utils/acc-iban-guid';
import { useGetBranchesMultiRoleQuery_MultiRole } from 'domains/branches/queries/branch.query';
import {
  TCounterpartyAvailableBranchesResData,
  TCounterpartyBranchesResData
} from 'domains/branches/types/branch.types';
import { TCounterpartiesListResData } from 'domains/counterparties/types/counterparty.types';
import { TScopeResData } from 'domains/roles/types/roles.types';
import { ROUTES_MAPPING } from 'navigation/constants/route.constants';
import {
  SFiltersContainer,
  SDangerActionItem,
} from './counterparty-processing.page.style';
import {
  EAccountManagementCounterpartyProcessingPageGetParams,
  EAccountManagementCounterpartyProcessingPageGetParamsActionTypes,
  ECounterpartyProcessingSearchForm,
  EModalType,
  ERowAction,
} from './enums/counerparty-processing.enums';
import CreateEditProcessingAccountModal from './modals/create-edit-processing.modal';
import { getColumns } from './table/conterparty-processing.columns';
import {
  EmissionAccountRecord,
  ItemClickEventHandler,
  TAccountManagementCounterpartyProcessingPageLoaderData,
  TCounterpartyProcessingModalData,
} from './types/counterparty-processing.types';

type TPaginationConfig = {
  page: number;
  pageSize: number;
};

const AccountManagementCounterpartyProcessingPage = (): ReactElement => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [form] = useForm();
  const loaderData = useLoaderData() as TAccountManagementCounterpartyProcessingPageLoaderData;
  const [paginationData, setPaginationData] = useState<TPaginationConfig>({
    page: DEFAULT_PAGE,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [selectedCounterparty, setSelectedCounterparty] = useAtom(selectedCounterpartyAtom);
  const [selectedBranch, setSelectedBranch] = useState('');
  const [selectedAccountNumberIban, setSelectedAccountNumberIban] = useState({});
  const [modalData, setModalData] = useState<TCounterpartyProcessingModalData | {}>({});

  useEffect(() => {
    if (!selectedCounterparty) {
      setSelectedCounterparty(IS_ADMIN_FE ? loaderData.defaultSelectedCounterparty : ENV_COUNTERPARTY_GUID);
    }

    form.setFields([
      {
        name: ECounterpartyProcessingSearchForm.CounterpartyName,
        value: IS_ADMIN_FE ? loaderData.defaultSelectedCounterparty : ENV_COUNTERPARTY_GUID,
      },
    ]);
  }, []);

  const {
    data: counterpartiesAccountsData,
    isLoading: isCounterpartiesAccountsDataLoading,
    isError: isCounterpartiesAccountsDataError,
    error: counterpartiesAccountsDataError,
  } = useCounterpartyAccountsListQuery(
    selectedCounterparty,
    {
      limit: paginationData.pageSize,
      offset: (paginationData.page - 1) * paginationData.pageSize,
      account_kinds: [EAccountKind.Processing],
      ...selectedAccountNumberIban,
    },
    {
      placeholderData: keepPreviousData,
      refetchOnMount: false,
      enabled: !!selectedCounterparty,
    },
  );

  const {
    data: branchesData,
    isError: isBranchesDataError,
    isLoading: isBranchesDataLoading,
    error: branchesDataError,
  } = useGetBranchesMultiRoleQuery_MultiRole(selectedCounterparty, {}, loaderData.hasAccessForAllBranches, {
    refetchOnMount: false,
    select: (data: any) => {
      const redoData = {
        ...data,
        ...(loaderData.hasAccessForAllBranches ? { branches: data.list } : null),
      };

      return redoData;
    },
  });

  const {
    data: counterpartiesBranchAccountsData,
    isLoading: isCounterpartiesBranchAccountsDataLoading,
    isError: isCounterpartiesBranchAccountsDataError,
    error: counterpartiesBranchAccountsDataError,
  } = useCounterpartyBranchAccountsListQuery(
    selectedCounterparty,
    selectedBranch,
    {
      limit: paginationData.pageSize,
      offset: (paginationData.page - 1) * paginationData.pageSize,
      account_kinds: [EAccountKind.Processing],
      ...selectedAccountNumberIban,
    },
    {
      placeholderData: keepPreviousData,
      refetchOnMount: false,
      enabled: !!selectedCounterparty && !!selectedBranch,
    },
  );

  const isRequestsError =
    isCounterpartiesAccountsDataError || isBranchesDataError || isCounterpartiesBranchAccountsDataError;
  const requestError =
    counterpartiesAccountsDataError || branchesDataError || counterpartiesBranchAccountsDataError;

  useEffect(() => {
    if (isRequestsError) {
      console.error(requestError);
    }
  }, [isRequestsError, requestError]);

  const counterpartyOptions = (loaderData?.counterpartiesListData?.counterparties || [])
    .map((counterparty) => ({
      label: counterparty.name,
      value: counterparty.guid,
    }))
    .filter(counterparty => IS_ADMIN_FE
        ? counterparty.value !== ENV_COUNTERPARTY_GUID
        : counterparty.value === ENV_COUNTERPARTY_GUID
    );

  const branchesOptions = ((branchesData as TCounterpartyAvailableBranchesResData)?.branches || []).map((branch) => ({
    label: branch.name,
    value: branch.guid,
  }));

  useEffect(() => {
    const searchParams = new URLSearchParams(document.location.search);
    const pageAction = searchParams.get(EAccountManagementCounterpartyProcessingPageGetParams.Action);

    if (pageAction === EAccountManagementCounterpartyProcessingPageGetParamsActionTypes.Create) {
      setModalData({
        type: EModalType.Create,
        rowData: {
          counterpartyOptions,
          selectedCounterparty: IS_ADMIN_FE ? selectedCounterparty : ENV_COUNTERPARTY_GUID,
        },
      });
    }
  }, [document.location.search]);

  const tableData: EmissionAccountRecord[] =
    ((!selectedBranch
        ? counterpartiesAccountsData?.accounts
        : counterpartiesBranchAccountsData?.accounts)
    || []).map((item, index) => ({
      ...item,
      // id: offset + index + 1, // TODO:
      id: index + 1, // TODO:
      currency: 'UAH', // mocks
      branch_name: item?.branch?.name || '',
      account_name: item?.account_kind,
      actual_balance: '50 000', // mocks
      issue_burn_saldo: '39 912', // mocks
      reserve_acc: '1 543 217', // mocks
      account_status: 1, // mocks
  }));

  const actions: MenuProps['items'] = [
    {
      key: ERowAction.Edit,
      label: t('accountManagement.counterpartyProcessing.table.menu.action.edit'),
    },
    {
      key: ERowAction.Delete,
      label:
        <SDangerActionItem>{t('accountManagement.counterpartyProcessing.table.menu.action.delete')}</SDangerActionItem>,
    },
  ];

  const onItemActionClick: ItemClickEventHandler = ({ item }) => {
    const fn: MenuProps['onClick'] = ({ key }) => {
      switch (key) {
        case ERowAction.Edit: {
          setModalData({
            type: EModalType.Edit,
            rowData: {
              counterpartyOptions,
              selectedCounterparty,
              record: item,
            },
          });
          break;
        }
        case ERowAction.Delete: {
          throw new Error('Deletion is not implemented');
          break;
        }
        default: {
          throw new Error(`click handler for menu item with key [${key}] is not implemented`);
        }
      }
    };
    return fn;
  };

  const columns = getColumns({
    menuItems: actions,
    onItemActionClick,
  });

  const onPaginationChangeHandler = (pageParam: number, pageSizeParam: number): void => {
    setPaginationData({
      page: pageParam,
      pageSize: pageSizeParam,
    });
  };

  const paginationConfig = {
    style: {
      marginRight: 30,
    },
    showQuickJumper: true,
    showSizeChanger: true,
    total: !selectedBranch ? counterpartiesAccountsData?.total : counterpartiesBranchAccountsData?.total,
    pageSize: paginationData.pageSize,
    current: paginationData.page,
    onChange: onPaginationChangeHandler,
  };

  const onCancelModalHandler = (): void => {
    setModalData({});
    navigate(ROUTES_MAPPING.PROTECTED.ACCOUNT_MANAGEMENT_COUNTERPARTY_PROCESSING);
  };

  const onValuesChangeFormHandler =
    async (changedValues: Record<ECounterpartyProcessingSearchForm, string>): Promise<void> => {
    if (ECounterpartyProcessingSearchForm.CounterpartyName in changedValues) {
      setSelectedCounterparty(changedValues.counterpartyName);
      setSelectedBranch('');
      form.setFieldValue(ECounterpartyProcessingSearchForm.BranchName, '');
    }
    if (ECounterpartyProcessingSearchForm.BranchName in changedValues) {
      setSelectedBranch(changedValues.branch_name);
    }
  }

  const onFinishFormHandler = async (values: Record<ECounterpartyProcessingSearchForm, string>): Promise<void> => {
    try {
      const { counterpartyName, wallet_account_guids, branch_name } = values;
      setSelectedCounterparty(counterpartyName);

      if (wallet_account_guids !== undefined) {
        const { filter } = makeAccountFilterByGUIDOrIBAN(wallet_account_guids);

        if (filter.field) {
          setSelectedAccountNumberIban({ [filter.field]: filter.value });
        } else {
          setSelectedAccountNumberIban({});
        }
      }

      if (branch_name !== undefined) {
        setSelectedBranch(branch_name);
      }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <div>
      <SFiltersContainer>
        <Form
          form={form}
          layout="inline"
          onFinish={onFinishFormHandler}
          onValuesChange={onValuesChangeFormHandler}
        >
          <Space>
            <FormItem
              name={ECounterpartyProcessingSearchForm.CounterpartyName}
              hidden={IS_BANK_FE}
            >
              <Select
                style={{
                  width: 272,
                }}
                options={counterpartyOptions}
              />
            </FormItem>
            <FormItem
              name={ECounterpartyProcessingSearchForm.BranchName}
            >
              <Select
                style={{ width: '280px' }}
                placeholder={t('accountManagement.counterpartyProcessing.filterButtonBranch.placeholder') as string}
                options={branchesOptions}
                loading={isBranchesDataLoading}
                allowClear
              />
            </FormItem>
            <FormItem
              name={ECounterpartyProcessingSearchForm.WalletAccountGUID}
              rules={[
                (): { validator(_: any, value: any): Promise<any> } => ({
                  validator(_, value): Promise<any> {
                    if (value !== undefined && value.length > 0) {
                      const { error } = makeAccountFilterByGUIDOrIBAN(value);

                      if (error) {
                        return Promise.reject(new Error(error));
                      }
                    }

                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <Input
                placeholder={t('accountManagement.counterpartyProcessing.search.accountNumberOrIban.placeholder')!}
                suffix={<SearchOutlined />}
                minLength={2}
                allowClear
                rootClassName="iban-input"
              />
            </FormItem>
            <Button
              loading={isCounterpartiesAccountsDataLoading}
              htmlType="submit"
              type="primary"
            >
              {t('accountManagement.counterpartyProcessing.button.search')}
            </Button>
          </Space>
        </Form>
      </SFiltersContainer>
      <Table
        loading={isCounterpartiesAccountsDataLoading || isCounterpartiesBranchAccountsDataLoading}
        pagination={paginationConfig}
        rowKey={EEmissionAccountColumnKeys.ID}
        dataSource={tableData}
        columns={columns}
      />
      <CreateEditProcessingAccountModal
        isOpen={!!Object.keys(modalData).length}
        modalData={modalData as TCounterpartyProcessingModalData}
        onCancelModalHandler={onCancelModalHandler}
      />
    </div>
  );
};

export const loader = async (): Promise<{
  defaultSelectedCounterparty: string,
  counterpartiesListData: TCounterpartiesListResData | {},
  counterpartyAccountsData: TAccountsListResData | {},
  hasAccessForAllBranches: boolean,
  branchesData: {},
}> => {
  try {
    const counterpartiesListQuery = queryKeysClient[EQueryKeys.CounterpartyQueryKeys].getCounterpartiesList();
    const counterpartiesListData: TCounterpartiesListResData =
      queryClient.getQueryData(counterpartiesListQuery.queryKey) ??
      (await queryClient.fetchQuery(counterpartiesListQuery));

    const filteredCounterpartyList = (counterpartiesListData as TCounterpartiesListResData).counterparties
      .filter(({ guid }) => guid !== ENV_COUNTERPARTY_GUID);

    const selectCounterpartyFromLocalStore = localStorage.getItem(
      PERSISTENCE_KEYS.ACCOUNT_MANAGEMENT_SELECTED_COUNTERPARTY
    );

    const checkedCounterpartyGuid =
      filteredCounterpartyList.find(item => item.guid === selectCounterpartyFromLocalStore)?.guid;

    const defaultSelectedCounterparty = checkedCounterpartyGuid
      || filteredCounterpartyList[filteredCounterpartyList.length - 1].guid;

    const counterpartyAccountsQuery = queryKeysClient[EQueryKeys.AccountQueryKeys].getCounterpartyAccounts(
      defaultSelectedCounterparty,
      {
        offset: (DEFAULT_PAGE - 1) * DEFAULT_PAGE_SIZE,
        limit: DEFAULT_PAGE_SIZE,
        account_kinds: [EAccountKind.Processing],
      }
    );

    const counterpartyAccountsData: TAccountsListResData =
      queryClient.getQueryData(counterpartyAccountsQuery.queryKey) ??
      (await queryClient.fetchQuery(counterpartyAccountsQuery));

    const queryScopeListData =
      queryKeysClient[EQueryKeys.RbacQueryKeys].getCounterpartyScopesList(ENV_COUNTERPARTY_GUID);
    const scopeListData: TScopeResData =
      queryClient.getQueryData(queryScopeListData.queryKey) ??
      (await queryClient.fetchQuery(queryScopeListData));

    const hasAccessForAllBranches =
      ((scopeListData as TScopeResData).scopes.find(({ scope }) => scope === 'employees')?.is_allowed &&
        (scopeListData as TScopeResData).scopes.find(({ scope }) => scope === 'branches')?.is_allowed) ||
      false;

    const getBranchesMultiRoleQuery = queryKeysClient[EQueryKeys.BranchQueryKeys].getBranches_MultiRole(
      ENV_COUNTERPARTY_GUID,
      {},
      hasAccessForAllBranches,
    );
    const branchesData: TCounterpartyBranchesResData | TCounterpartyAvailableBranchesResData =
      queryClient.getQueryData(getBranchesMultiRoleQuery.queryKey) ??
      (await queryClient.fetchQuery(getBranchesMultiRoleQuery));

    return {
      counterpartiesListData,
      defaultSelectedCounterparty,
      counterpartyAccountsData,
      branchesData,
      hasAccessForAllBranches,
    };
  } catch (error) {
    console.error(error);

    return {
      hasAccessForAllBranches: false,
      counterpartiesListData: {},
      defaultSelectedCounterparty: '',
      counterpartyAccountsData: {},
      branchesData: {},
    };
  }
};

export default AccountManagementCounterpartyProcessingPage;
